import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

class ExcelExporter {
  constructor() {
    this.workbook = new ExcelJS.Workbook();
    this.worksheet = null;
    this.columns = [];
    this.currentRowIndex = 1;
    this.isExporting = false;
    this.cancelToken = { cancel: false };
    this.totalRows = 0;
    this.processedRows = 0;

    // 存储每列的最大宽度
    this.maxColumnWidths = [];

    // 公共样式定义
    this.commonStyles = {
      // 公共边框样式
      border: {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      },

      // 标题行样式
      title: {
        font: { size: 14, bold: true },
        alignment: { vertical: "middle", horizontal: "center" },
        height: 35,
      },

      // 表头样式
      header: {
        font: { size: 11, bold: true },
        alignment: { vertical: "middle", horizontal: "center" },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFEEEEEE" } },
        height: 25,
      },

      // 数据行样式
      dataRow: {
        alignment: { vertical: "middle" },
        height: 25,
      },

      // 斑马纹样式
      zebraStriping: {
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8F8F8" } },
      },
    };

    // 预定义对齐样式（提高性能）
    this.alignStyles = {
      left: { horizontal: "left" },
      center: { horizontal: "center" },
      right: { horizontal: "right" },
    };
  }

  /**
   * 初始化工作表
   * @param {string} sheetName 工作表名称
   */
  initWorksheet(sheetName = "Sheet1") {
    this.worksheet = this.workbook.addWorksheet(sheetName);
    this.currentRowIndex = 1;
    this.maxColumnWidths = []; // 重置最大宽度数组
  }

  /**
   * 设置标题行
   * @param {string} title 标题文本
   * @param {number} colSpan 合并列数
   */
  setTitle(title, colSpan) {
    if (!this.worksheet) this.initWorksheet();

    // 合并单元格
    this.worksheet.mergeCells(this.currentRowIndex, 1, this.currentRowIndex, colSpan);

    // 设置标题单元格
    const titleCell = this.worksheet.getCell(this.currentRowIndex, 1);
    titleCell.value = title;

    // 应用公共标题样式
    titleCell.style = {
      font: this.commonStyles.title.font,
      alignment: this.commonStyles.title.alignment,
      border: this.commonStyles.border,
    };

    // 设置行高
    this.worksheet.getRow(this.currentRowIndex).height = this.commonStyles.title.height;

    this.currentRowIndex++;
  }

  /**
   * 设置表头
   * @param {Array} headers 表头配置数组
   * 每个header对象可包含以下属性：
   *   title: 表头标题
   *   key: 数据字段键
   *   align: 对齐方式 ('left', 'center', 'right')
   *   width: 列宽（可选）
   */
  setHeaders(headers) {
    if (!this.worksheet) this.initWorksheet();

    this.columns = headers;

    // 初始化最大宽度数组（基于表头标题）
    this.maxColumnWidths = headers.map((header) => {
      return this.calculateWidth(header.title);
    });

    // 添加表头行
    const headerValues = headers.map((header) => header.title);
    const headerRow = this.worksheet.addRow(headerValues);

    // 应用公共表头样式
    headerRow.eachCell((cell) => {
      cell.style = {
        font: this.commonStyles.header.font,
        alignment: this.commonStyles.header.alignment,
        fill: this.commonStyles.header.fill,
        border: this.commonStyles.border,
      };
    });

    // 设置行高
    headerRow.height = this.commonStyles.header.height;

    this.currentRowIndex++;
  }

  /**
   * 高效计算字符串宽度
   * @param {string} str 输入字符串
   * @returns {number} 计算后的宽度
   */
  calculateWidth(str) {
    let width = 0;
    const len = Math.min(str.length, 1000); // 最多计算1000个字符
    for (let i = 0; i < len; i++) {
      width += str.charCodeAt(i) > 255 ? 2 : 1;
    }
    return width;
  }

  /**
   * 添加数据行（高性能版本）
   * @param {Array} data 数据数组
   * @param {Function} onDataReceived 数据接收完成回调
   */
  addDataRows(data, onDataReceived) {
    // 关键修复：检查取消状态
    if (this.cancelToken.cancel) {
      if (onDataReceived) onDataReceived();
      return;
    }

    if (!this.worksheet) this.initWorksheet();

    // 批量添加数据行（不应用样式）
    const rows = [];
    for (let i = 0; i < data.length; i++) {
      const rowData = data[i];
      const rowValues = new Array(this.columns.length);
      for (let j = 0; j < this.columns.length; j++) {
        rowValues[j] = rowData[this.columns[j].key];
      }
      rows.push(rowValues);
    }

    // 一次性添加所有行（提高性能）
    this.worksheet.addRows(rows);

    // 增量更新最大宽度
    for (let i = 0; i < data.length; i++) {
      const rowData = data[i];
      for (let j = 0; j < this.columns.length; j++) {
        const col = this.columns[j];
        const value = rowData[col.key];

        // 跳过固定宽度的列
        if (col.width) continue;

        if (value != null) {
          const strValue = String(value);
          const width = this.calculateWidth(strValue);

          // 更新最大宽度
          if (width > this.maxColumnWidths[j]) {
            this.maxColumnWidths[j] = width;
          }
        }
      }
    }

    // 更新行数统计
    this.totalRows += data.length;
    this.processedRows += data.length;

    // 通知数据接收完成
    if (onDataReceived) onDataReceived();
  }

  /**
   * 应用样式到所有行（高性能批量应用）
   */
  applyStyles() {
    if (!this.worksheet) return;

    // 设置列宽（优先使用传入的宽度，否则使用动态计算的最大宽度）
    for (let i = 0; i < this.columns.length; i++) {
      const col = this.columns[i];
      const column = this.worksheet.getColumn(i + 1);

      if (col.width) {
        // 使用传入的宽度
        column.width = col.width;
      } else {
        // 使用动态计算的最大宽度
        const maxWidth = this.maxColumnWidths[i] || 0;
        // 设置最小宽度为10，最大宽度为50
        column.width = Math.min(Math.max(maxWidth, 10), 50);
      }
      column.width + 5;
    }

    // 应用数据行样式
    const rowCount = this.worksheet.rowCount;
    const commonBorder = this.commonStyles.border;
    const zebraFill = this.commonStyles.zebraStriping.fill;
    const rowHeight = this.commonStyles.dataRow.height;

    // 从第3行开始（跳过标题和表头）
    for (let rowNumber = 3; rowNumber <= rowCount; rowNumber++) {
      const row = this.worksheet.getRow(rowNumber);

      // 设置行高
      row.height = rowHeight;

      // 应用单元格样式
      for (let colNumber = 1; colNumber <= this.columns.length; colNumber++) {
        const colIndex = colNumber - 1;
        const colConfig = this.columns[colIndex];
        const cell = row.getCell(colNumber);

        // 创建样式对象
        const cellStyle = {
          alignment: {
            vertical: "middle",
            ...this.alignStyles[colConfig.align || "center"],
          },
          border: commonBorder,
        };

        // 只在偶数行应用斑马纹背景色
        if (rowNumber % 2 === 0) {
          cellStyle.fill = zebraFill;
        }

        cell.style = cellStyle;
      }
    }
  }

  /**
   * 导出Excel文件
   * @param {string} filename 文件名
   * @returns {Promise} 导出结果Promise
   */
  async export(filename = "export-data.xlsx") {
    if (this.isExporting) {
      throw new Error("导出正在进行中，请等待完成");
    }

    this.isExporting = true;

    try {
      // 应用样式（批量处理）
      this.applyStyles();

      const buffer = await this.workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, filename);
      this.isExporting = false;
      return true;
    } catch (error) {
      this.isExporting = false;
      throw error;
    }
  }

  /**
   * 获取导出进度
   * @returns {number} 导出进度百分比
   */
  getProgress() {
    if (this.totalRows === 0) return 0;
    return Math.round((this.processedRows / this.totalRows) * 100);
  }

  /**
   * 终止导出并清空
   */
  cancelAndClear() {
    this.cancelToken.cancel = true;
    this.clear();
  }

  /**
   * 清空导出状态
   */
  clear() {
    this.workbook = new ExcelJS.Workbook();
    this.worksheet = null;
    this.columns = [];
    this.currentRowIndex = 1;
    this.isExporting = false;
    this.cancelToken = { cancel: false };
    this.totalRows = 0;
    this.processedRows = 0;
    this.maxColumnWidths = [];
  }
}

export default ExcelExporter;
